import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Role constants (compatible with backend)
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CONSULTANT: 'CONSULTANT',
  CONSULTANT_ASSOCIATE: 'CONSULTANT_ASSOCIATE',  // NEW: Associates in a consultancy
  CONTRACTOR_ADMIN: 'CONTRACTOR_ADMIN',  // Renamed from CLIENT_ADMIN
  BID_MANAGER: 'BID_MANAGER',
  REVIEWER: 'REVIEWER',
};

// Legacy role mapping for backwards compatibility
export const LEGACY_ROLES = {
  super_admin: 'SUPER_ADMIN',
  consultant: 'CONSULTANT',
  consultant_associate: 'CONSULTANT_ASSOCIATE',
  client_admin: 'CONTRACTOR_ADMIN',  // Map old to new
  contractor_admin: 'CONTRACTOR_ADMIN',
  bid_manager: 'BID_MANAGER',
  reviewer: 'REVIEWER',
};

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Admin (Platform Owner)',
  [ROLES.CONSULTANT]: 'Consultant (Service Provider)',
  [ROLES.CONSULTANT_ASSOCIATE]: 'Consultant Associate',
  [ROLES.CONTRACTOR_ADMIN]: 'Contractor Admin',
  [ROLES.BID_MANAGER]: 'Bid Manager/Writer',
  [ROLES.REVIEWER]: 'Reviewer',
};

// Default workspace IDs for demo purposes (fallback)
const DEFAULT_WORKSPACE_ID = 'acme-corp';
const DEFAULT_CONSULTANCY_ID = 'apex-consulting';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  // True until the initial /auth/me bootstrap finishes. Route guards should
  // wait on this instead of redirecting to /login on the first render tick.
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isLoggingOut = useRef(false);

  // Check for existing session on mount.
  // Preferred path: HttpOnly cookie → /api/auth/me (token never exposed to JS).
  // Fast path: hydrate user state from cached profile in localStorage, then
  // validate with /auth/me. If validation fails, clear the stale cache.
  useEffect(() => {
    const storedUser = localStorage.getItem('auth_user');

    // Clean up legacy token storage from earlier versions.
    localStorage.removeItem('auth_token');

    const hydrateFromApiUser = (apiUser) => {
      setUser({
        id: apiUser.id,
        email: apiUser.email,
        username: `${apiUser.first_name} ${apiUser.last_name}`,
        firstName: apiUser.first_name,
        lastName: apiUser.last_name,
        role: apiUser.role,
        workspaceId: apiUser.organization_slug,
        organizationId: apiUser.organization_id,
        organizationName: apiUser.organization_name,
        organizationType: apiUser.organization_type,
        isTrial: apiUser.is_trial,
        trialExpiresAt: apiUser.trial_expires_at,
        subscriptionPlan: apiUser.subscription_plan,
        policyAcceptedAt: apiUser.policy_accepted_at,
        policyVersion: apiUser.policy_version,
        isJWTAuth: true,
      });
      setIsAuthenticated(true);
      // Sentinel so components that still gate on `authToken` see the user as
      // authenticated. The real JWT lives in the HttpOnly cookie; this string
      // is only a presence flag. The backend's cookie→header bridge recognizes
      // it and substitutes the cookie's JWT on each request.
      setAuthToken('COOKIE_SESSION');
    };

    // 1. Fast-boot from cached profile so the UI renders immediately.
    if (storedUser) {
      try {
        hydrateFromApiUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('auth_user');
      }
    }

    // 2. Validate (or cold-bootstrap) via /auth/me using the HttpOnly cookie.
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('no session');
        const apiUser = await res.json();
        hydrateFromApiUser(apiUser);
        localStorage.setItem('auth_user', JSON.stringify(apiUser));
      } catch {
        // No valid cookie — discard any stale cached profile.
        if (storedUser) {
          localStorage.removeItem('auth_user');
          setUser(null);
          setIsAuthenticated(false);
        }
        // Preserve legacy mock-auth flows
        const legacyUser = sessionStorage.getItem('pwin_user');
        if (legacyUser && !storedUser) {
          try {
            setUser(JSON.parse(legacyUser));
            setIsAuthenticated(true);
          } catch {
            sessionStorage.removeItem('pwin_user');
          }
        }
      } finally {
        setIsCheckingAuth(false);
      }
    })();
  }, []);

  // JWT Login - called after successful API login.
  // The JWT itself lives in the HttpOnly cookie set by the backend; we do NOT
  // store it in localStorage. Only non-sensitive profile data is cached locally
  // to skip a loading spinner on the next page reload.
  const loginWithJWT = useCallback((token, apiUser) => {
    isLoggingOut.current = false;
    
    const userData = {
      id: apiUser.id,
      email: apiUser.email,
      username: `${apiUser.first_name} ${apiUser.last_name}`,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      role: apiUser.role,
      workspaceId: apiUser.organization_slug,
      organizationId: apiUser.organization_id,
      organizationName: apiUser.organization_name,
      organizationType: apiUser.organization_type,
      // Trial info for expiry guard
      isTrial: apiUser.is_trial,
      trialExpiresAt: apiUser.trial_expires_at,
      subscriptionPlan: apiUser.subscription_plan,
      // Policy acceptance
      policyAcceptedAt: apiUser.policy_accepted_at,
      policyVersion: apiUser.policy_version,
      isJWTAuth: true,
      createdAt: new Date().toISOString(),
    };
    
    setUser(userData);
    // Keep the token in React state for the one remaining getAuthHeaders() use-case
    // (e.g. a few legacy direct fetchers). Not persisted to storage — page reload
    // loses it but the HttpOnly cookie continues to authenticate every request.
    setAuthToken(token);
    setIsAuthenticated(true);
    
    // Cache profile for fast UI boot on reload. NEVER cache the token.
    localStorage.setItem('auth_user', JSON.stringify(apiUser));
    localStorage.removeItem('auth_token');
    
    return userData;
  }, []);

  // Legacy mock login - for backwards compatibility
  const login = useCallback((username, role) => {
    isLoggingOut.current = false;
    
    // Normalize role to uppercase
    const normalizedRole = LEGACY_ROLES[role] || role;
    
    // Determine workspace based on role
    let workspaceId;
    if (normalizedRole === ROLES.SUPER_ADMIN) {
      workspaceId = 'admin';
    } else if (normalizedRole === ROLES.CONSULTANT) {
      workspaceId = DEFAULT_CONSULTANCY_ID;
    } else {
      workspaceId = DEFAULT_WORKSPACE_ID;
    }
    
    const userData = {
      id: `user-${Date.now()}`,
      username,
      role: normalizedRole,
      workspaceId,
      isMockAuth: true,
      createdAt: new Date().toISOString(),
    };
    
    setUser(userData);
    setIsAuthenticated(true);
    
    // Store in session for persistence
    sessionStorage.setItem('pwin_user', JSON.stringify(userData));
    
    return userData;
  }, []);

  const logout = useCallback(() => {
    isLoggingOut.current = true;
    setUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);

    // Clear all auth storage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    sessionStorage.removeItem('pwin_user');

    // Notify other contexts (RiskAssessment, Pursuit, etc.) to wipe any
    // tenant-scoped state they hold in React memory. This prevents data
    // bleed across tenants in the same browser tab (e.g. logging out as
    // one user and back in as another would otherwise inherit the prior
    // session's `currentPursuit`/Fit Score).
    try {
      window.dispatchEvent(new CustomEvent('fedbd:auth-cleared'));
    } catch {
      // ignore (older browsers / SSR)
    }

    // Tell backend to clear the HttpOnly auth cookie.
    // Fire-and-forget — if it fails the cookie will expire naturally.
    fetch(`${API_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, []);

  // Check for existing session (legacy helper — returns cached profile only).
  // The JWT itself is in an HttpOnly cookie and never exposed to JS.
  const checkSession = useCallback(() => {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        return {
          id: userData.id,
          email: userData.email,
          username: `${userData.first_name} ${userData.last_name}`,
          role: userData.role,
          workspaceId: userData.organization_slug,
          isJWTAuth: true,
        };
      } catch {
        return null;
      }
    }

    // Fall back to legacy session
    const legacy = sessionStorage.getItem('pwin_user');
    if (legacy) {
      try {
        return JSON.parse(legacy);
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  // Get auth headers for API calls
  const getAuthHeaders = useCallback(() => {
    if (authToken) {
      return {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };
    }
    return { 'Content-Type': 'application/json' };
  }, [authToken]);

  // Check if trial has expired
  const isTrialExpired = useCallback(() => {
    if (!user) return false;
    // Super Admins and Consultants bypass trial check
    if (user.role === 'SUPER_ADMIN' || user.role === 'CONSULTANT') return false;
    // Not on trial
    if (!user.isTrial) return false;
    // Check expiration
    if (user.trialExpiresAt) {
      const expiresAt = new Date(user.trialExpiresAt);
      return new Date() > expiresAt;
    }
    return false;
  }, [user]);

  // Update user's policy acceptance status
  const updatePolicyAcceptance = useCallback((acceptedAt, version) => {
    setUser(prev => ({
      ...prev,
      policyAcceptedAt: acceptedAt,
      policyVersion: version
    }));
  }, []);

  const value = {
    user,
    isAuthenticated,
    isCheckingAuth,
    authToken,
    login,           // Legacy mock login
    loginWithJWT,    // JWT login
    logout,
    checkSession,
    getAuthHeaders,
    isTrialExpired,  // Trial expiry check
    updatePolicyAcceptance, // Update policy acceptance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
